import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Star, Shield, Users, CheckCircle, AlertTriangle, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

// Use optimized WebP from storage - preloaded in index.html for fast LCP
const heroImage = "https://ncsmwvjhmrxtwfylqgjp.supabase.co/storage/v1/object/public/site-assets/hero-image.jpeg";

interface Review {
  id: string;
  name: string;
  rating: number;
  text: string;
}

const Hero = () => {
  const [floatingReview, setFloatingReview] = useState<Review | null>(null);

  useEffect(() => {
    const fetchRandomReview = async () => {
      try {
        const { data, error } = await supabase
          .from('reviews')
          .select('id, name, rating, text')
          .eq('is_active', true)
          .gte('rating', 4)
          .order('created_at', { ascending: false })
          .limit(20);

        if (error) {
          console.error('Error fetching reviews:', error);
          return;
        }

        if (data && data.length > 0) {
          const randomIndex = Math.floor(Math.random() * data.length);
          setFloatingReview(data[randomIndex]);
        }
      } catch (err) {
        console.error('Error in fetchRandomReview:', err);
      }
    };

    fetchRandomReview();
  }, []);

  const truncateComment = (comment: string, maxLength: number = 80) => {
    if (comment.length <= maxLength) return comment;
    return comment.substring(0, maxLength).trim() + '...';
  };

  return (
    <section className="relative min-h-screen overflow-hidden pt-20 lg:pt-24">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-teal-light via-background to-cream bg-gradient-animated" />
      
      {/* Background decorative elements with animations */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse-soft" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse-soft animation-delay-400" />
      <div className="absolute top-1/2 left-1/4 w-48 h-48 bg-primary/5 rounded-full blur-2xl animate-float" />
      
      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[calc(100vh-5rem)] py-12 lg:py-20">
          {/* Content */}
          <div className="space-y-8">
            {/* Data-backed trust badge with pulse */}
            <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 shadow-custom-sm animate-fade-in bg-teal-light hover:shadow-custom-md transition-shadow cursor-default group">
              <AlertTriangle className="w-4 h-4 text-teal-dark group-hover:animate-wiggle" />
              <span className="text-sm font-medium text-teal-dark">
                145K+ credit complaints monthly — CFPB 2024
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight animate-fade-in animation-delay-200">
              Stop Guessing Why Your{" "}
              <span className="text-gradient">Score Won't Budge</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-muted-foreground max-w-xl animate-fade-in animation-delay-400">
              44% of credit reports have errors that hurt your score. Our DIY kit shows you exactly how to dispute them—the same process experts charge $1,500+ for.
            </p>

            {/* Compact stats with hover effect */}
            <div className="flex flex-wrap gap-6 animate-fade-in animation-delay-400 text-sm">
              <span className="text-muted-foreground group cursor-default">
                <span className="font-bold text-primary number-highlight">44%</span> of reports have errors
              </span>
              <span className="text-muted-foreground group cursor-default">
                <span className="font-bold text-primary number-highlight">26%</span> have serious mistakes
              </span>
            </div>

            {/* CTAs with glow effect */}
            <div className="space-y-4 animate-fade-in animation-delay-600">
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  variant="hero" 
                  size="xl" 
                  className="group btn-glow-animated"
                  onClick={() => document.querySelector('#kit-section')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Get the Complete DIY Kit ($99)
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button 
                  variant="heroOutline" 
                  size="xl"
                  className="hover:scale-[1.02] transition-transform"
                  onClick={() => document.querySelector('#templates-section')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Shop Single Templates ($29.99)
                </Button>
              </div>
              
              {/* Tertiary links */}
              <div className="flex flex-wrap gap-4 text-sm">
                <button
                  onClick={() => document.querySelector('#support-section')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 group"
                >
                  Packet Review (from $149)
                  <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </button>
                <Link 
                  to="/done-for-you"
                  className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 group"
                >
                  Done-For-You (from $750)
                  <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
              
              {/* Price anchor */}
              <p className="text-sm font-medium text-primary-dark">
                Best value: Full DIY kit for $99 — save $430+ vs. buying separately
              </p>
            </div>

            {/* Trust signals with staggered animation */}
            <div className="grid grid-cols-3 gap-6 pt-4">
              {[
                { icon: Shield, label: "FCRA Protected", delay: "0ms" },
                { icon: Users, label: "10K+ DIY Users", delay: "100ms" },
                { icon: CheckCircle, label: "Instant Access", delay: "200ms" },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 animate-fade-in opacity-0 hover-icon-pop group cursor-default"
                  style={{ animationDelay: `${600 + index * 100}ms`, animationFillMode: 'forwards' }}
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <item.icon className="w-5 h-5 text-primary icon-target" />
                  </div>
                  <span className="text-sm font-medium text-foreground">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative animate-slide-in-right animation-delay-400">
            {/* Floating testimonial with enhanced animation */}
            <div className="absolute -top-16 -right-4 bg-card rounded-xl p-4 shadow-custom-md max-w-xs animate-float hidden lg:block z-10 hover:shadow-custom-lg transition-shadow">
              <div className="flex gap-1 mb-2">
                {[...Array(floatingReview?.rating || 5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className="w-4 h-4 fill-accent text-accent" 
                    style={{ animationDelay: `${i * 100}ms` }}
                  />
                ))}
              </div>
              <p className="text-sm text-foreground font-medium">
                "{floatingReview ? truncateComment(floatingReview.text) : "Finally got those BS collections removed. Score jumped 52 points!"}"
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                — {floatingReview ? floatingReview.name : "Marcus T."}, verified buyer
              </p>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-custom-lg aspect-video group">
              <img 
                src={heroImage} 
                alt="Person confidently checking their improved credit score on phone" 
                width={665}
                height={374}
                fetchPriority="high"
                decoding="sync"
                loading="eager"
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 665px"
                style={{ aspectRatio: '16/9' }}
                className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700" 
              />
              {/* Overlay card with slide-up animation */}
              <div className="absolute bottom-6 left-6 right-6 bg-card/95 backdrop-blur-sm rounded-xl p-4 shadow-custom-md animate-slide-up-fade">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-teal flex items-center justify-center animate-pulse-soft">
                    <Zap className="w-6 h-6 text-primary-foreground fill-primary-foreground" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Fast Filing. No Delays.</p>
                    <p className="text-sm text-muted-foreground">Get started in minutes</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
